/**
 * EmailAddressTag — Renders an email address as a CRM entity chip when it
 * resolves, otherwise as a click-to-create affordance.
 *
 * Thin wrapper over `EntityBadgeByEmail` that pulls `orgId` from the route
 * when not supplied via props. `EntityBadgeByEmail` handles both states:
 *
 *   - Resolved as user/contact → solid chip, click opens slideout
 *   - Unresolved → email-as-label badge, click opens the create modal with
 *     just the email pre-filled
 *
 * When `orgId` can't be determined (no prop, not inside a route), falls back
 * to rendering the raw address as plain text since the search API requires
 * an org context.
 */

import { useParams } from 'react-router-dom';

import { EntityBadgeByEmail } from '@/lib/crm/components/badges/EntityBadgeByEmail';

export interface EmailAddressTagProps {
  /** Email address to display */
  address: string;
  /** Organization ID override (defaults to route param) */
  orgId?: number;
  /** Additional CSS classes applied to the wrapper span */
  className?: string;
}

export function EmailAddressTag({
  address,
  orgId: orgIdProp,
  className,
}: EmailAddressTagProps) {
  const { orgId: routeOrgId } = useParams<{ orgId: string }>();
  const orgIdNum = orgIdProp ?? (routeOrgId ? Number(routeOrgId) : undefined);

  if (!orgIdNum || !Number.isFinite(orgIdNum)) {
    return <span className={className}>{address}</span>;
  }

  return (
    <EntityBadgeByEmail
      email={address}
      orgId={orgIdNum}
      variant='inline'
      className={className}
    />
  );
}
